// Vim lessons - 30 unique lessons covering beginner to expert

const vimLessons = [
  // BEGINNER LESSONS (10)
  {
    id: 'vim-001',
    title: 'Open a File',
    description: 'Start Vim and open a file for editing.',
    task: 'Open a file called "config.txt" in Vim.',
    hint: 'Type vim followed by the filename.',
    example: 'vim myfile.txt',
    answer: ['vim config.txt'],
    tips: [
      'Vim starts in NORMAL mode',
      'If the file does not exist, Vim creates it when you save'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'vim',
      description: 'Open the Vim text editor with an optional file to edit.',
      syntax: 'vim [options] [file ...]',
      options: [
        { flag: '+[num]', description: 'Open file at specified line number' },
        { flag: '+/{pattern}', description: 'Open file at first match of pattern' },
        { flag: '-R', description: 'Read-only mode' },
        { flag: '-o [N]', description: 'Open N windows stacked horizontally' },
        { flag: '-O [N]', description: 'Open N windows stacked vertically' },
        { flag: '-p [N]', description: 'Open N tab pages' }
      ],
      examples: [
        { code: 'vim config.txt', description: 'Open config.txt for editing' },
        { code: 'vim +42 file.txt', description: 'Open file.txt at line 42' },
        { code: 'vim -R important.conf', description: 'Open file in read-only mode' },
        { code: 'vim -o file1.txt file2.txt', description: 'Open two files in split windows' }
      ]
    }
  },
  {
    id: 'vim-002',
    title: 'Enter Insert Mode',
    description: 'Switch from NORMAL mode to INSERT mode to start typing text.',
    task: 'Enter INSERT mode to start typing at the cursor position.',
    hint: 'Press a single key to enter insert mode.',
    example: 'Press a to insert after cursor',
    answer: ['i'],
    tips: [
      'i = insert before cursor',
      'a = append after cursor',
      'The mode shows at the bottom: -- INSERT --'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'i',
      description: 'Enter INSERT mode to type text before the cursor.',
      syntax: 'i',
      options: [
        { flag: 'i', description: 'Insert before cursor' },
        { flag: 'I', description: 'Insert at beginning of line' },
        { flag: 'a', description: 'Append after cursor' },
        { flag: 'A', description: 'Append at end of line' },
        { flag: 'o', description: 'Open new line below and enter insert mode' },
        { flag: 'O', description: 'Open new line above and enter insert mode' }
      ],
      examples: [
        { code: 'i', description: 'Start inserting text before the cursor' },
        { code: 'I', description: 'Insert at the beginning of the current line' },
        { code: 'a', description: 'Start inserting text after the cursor' },
        { code: 'A', description: 'Append text at the end of the current line' }
      ]
    }
  },
  {
    id: 'vim-003',
    title: 'Return to Normal Mode',
    description: 'Exit INSERT mode and return to NORMAL mode.',
    task: 'Return to NORMAL mode from INSERT mode.',
    hint: 'Press a key to escape insert mode.',
    example: 'Press the escape key',
    answer: ['Esc', 'ESC', '<Esc>'],
    tips: [
      'Esc always returns you to NORMAL mode',
      'Ctrl+[ also works as escape'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'Esc',
      description: 'Return to NORMAL mode from any other mode.',
      syntax: '<Esc>',
      options: [
        { flag: 'Esc', description: 'Exit current mode and return to NORMAL mode' },
        { flag: 'Ctrl+[', description: 'Alternative escape sequence' },
        { flag: 'Ctrl+c', description: 'Also exits insert mode (may cancel operations)' }
      ],
      examples: [
        { code: 'Esc', description: 'Exit INSERT mode and return to NORMAL mode' },
        { code: 'Ctrl+[', description: 'Alternative way to return to NORMAL mode' }
      ]
    }
  },
  {
    id: 'vim-004',
    title: 'Save a File',
    description: 'Write (save) the current file to disk.',
    task: 'Save the current file without closing Vim.',
    hint: 'Use a colon command to write.',
    example: ':w filename - saves as new name',
    answer: [':w'],
    tips: [
      ':w writes the file',
      ':w newname saves as a different filename'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: ':w',
      description: 'Write (save) the current buffer to disk.',
      syntax: ':w [filename]',
      options: [
        { flag: ':w', description: 'Save current file' },
        { flag: ':w filename', description: 'Save as new filename (save as)' },
        { flag: ':w!', description: 'Force save (override read-only)' },
        { flag: ':w >> file', description: 'Append buffer to file' },
        { flag: ':1,10w file', description: 'Write lines 1-10 to file' }
      ],
      examples: [
        { code: ':w', description: 'Save the current file' },
        { code: ':w backup.txt', description: 'Save current buffer as backup.txt' },
        { code: ':w!', description: 'Force save a read-only file' },
        { code: ':%w !sudo tee %', description: 'Save file with sudo permissions' }
      ]
    }
  },
  {
    id: 'vim-005',
    title: 'Quit Vim',
    description: 'Exit Vim and return to the terminal.',
    task: 'Quit Vim (assuming no unsaved changes).',
    hint: 'Use a colon command to quit.',
    example: ':q! - quit without saving',
    answer: [':q'],
    tips: [
      ':q quits if no changes',
      ':q! forces quit without saving'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: ':q',
      description: 'Quit Vim and return to the terminal.',
      syntax: ':q[!]',
      options: [
        { flag: ':q', description: 'Quit (fails if unsaved changes exist)' },
        { flag: ':q!', description: 'Quit without saving, discard changes' },
        { flag: ':qa', description: 'Quit all windows' },
        { flag: ':qa!', description: 'Quit all windows, discard all changes' }
      ],
      examples: [
        { code: ':q', description: 'Quit Vim (only works if no unsaved changes)' },
        { code: ':q!', description: 'Force quit, discarding any unsaved changes' },
        { code: ':qa', description: 'Quit all open windows/tabs' }
      ]
    }
  },
  {
    id: 'vim-006',
    title: 'Save and Quit',
    description: 'Save the file and exit Vim in one command.',
    task: 'Save the file and quit Vim.',
    hint: 'Combine write and quit commands.',
    example: 'ZZ also saves and quits',
    answer: [':wq', ':x', 'ZZ'],
    tips: [
      ':wq = write then quit',
      ':x or ZZ only writes if changes were made'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: ':wq',
      description: 'Write the file and quit Vim in one command.',
      syntax: ':wq | :x | ZZ',
      options: [
        { flag: ':wq', description: 'Write file and quit (always writes)' },
        { flag: ':wq!', description: 'Force write and quit' },
        { flag: ':x', description: 'Write only if modified, then quit' },
        { flag: 'ZZ', description: 'Same as :x (write if changed and quit)' },
        { flag: ':wqa', description: 'Write all buffers and quit all windows' }
      ],
      examples: [
        { code: ':wq', description: 'Save the file and exit Vim' },
        { code: ':x', description: 'Save if modified and exit' },
        { code: 'ZZ', description: 'Quick save and quit from normal mode' },
        { code: ':wqa', description: 'Save all open files and quit Vim' }
      ]
    }
  },
  {
    id: 'vim-007',
    title: 'Quit Without Saving',
    description: 'Discard all changes and exit Vim.',
    task: 'Quit Vim and discard all unsaved changes.',
    hint: 'Force quit with an exclamation mark.',
    example: ':e! - revert to saved version',
    answer: [':q!'],
    tips: [
      '! forces the command',
      'Be careful - changes are permanently lost'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: ':q!',
      description: 'Quit Vim without saving, discarding all changes.',
      syntax: ':q!',
      options: [
        { flag: ':q!', description: 'Force quit without saving current buffer' },
        { flag: ':qa!', description: 'Force quit all windows without saving' },
        { flag: ':e!', description: 'Revert to last saved version (discard changes, stay in Vim)' },
        { flag: ':cq', description: 'Quit with error code (useful for git commit abort)' }
      ],
      examples: [
        { code: ':q!', description: 'Discard changes and exit Vim' },
        { code: ':qa!', description: 'Discard changes in all buffers and exit' },
        { code: ':e!', description: 'Discard changes but keep Vim open' }
      ]
    }
  },
  {
    id: 'vim-008',
    title: 'Basic Navigation',
    description: 'Move the cursor using hjkl keys in NORMAL mode.',
    task: 'Which key moves the cursor DOWN one line?',
    hint: 'Think of h-j-k-l as left-down-up-right.',
    example: 'h=left, l=right, k=up',
    answer: ['j'],
    tips: [
      'j looks like a down arrow with a hook',
      'You can also use arrow keys, but hjkl is faster'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'hjkl',
      description: 'Navigate the cursor using home row keys in NORMAL mode.',
      syntax: 'h | j | k | l',
      options: [
        { flag: 'h', description: 'Move cursor left one character' },
        { flag: 'j', description: 'Move cursor down one line' },
        { flag: 'k', description: 'Move cursor up one line' },
        { flag: 'l', description: 'Move cursor right one character' },
        { flag: '0', description: 'Move to beginning of line' },
        { flag: '$', description: 'Move to end of line' },
        { flag: 'w', description: 'Move forward one word' },
        { flag: 'b', description: 'Move backward one word' }
      ],
      examples: [
        { code: 'j', description: 'Move cursor down one line' },
        { code: '5j', description: 'Move cursor down 5 lines' },
        { code: '10k', description: 'Move cursor up 10 lines' },
        { code: '3w', description: 'Move forward 3 words' }
      ]
    }
  },
  {
    id: 'vim-009',
    title: 'Delete a Character',
    description: 'Delete the character under the cursor.',
    task: 'Delete the single character under the cursor.',
    hint: 'A single key deletes one character.',
    example: 'X deletes character before cursor',
    answer: ['x'],
    tips: [
      'x = delete character at cursor',
      'X = delete character before cursor'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'x',
      description: 'Delete the character under or before the cursor.',
      syntax: '[count]x | [count]X',
      options: [
        { flag: 'x', description: 'Delete character under cursor' },
        { flag: 'X', description: 'Delete character before cursor (backspace)' },
        { flag: '5x', description: 'Delete 5 characters starting at cursor' },
        { flag: 'xp', description: 'Transpose two characters (delete and paste)' }
      ],
      examples: [
        { code: 'x', description: 'Delete the character under the cursor' },
        { code: 'X', description: 'Delete the character before the cursor' },
        { code: '5x', description: 'Delete 5 characters from cursor position' },
        { code: 'xp', description: 'Swap current character with next one' }
      ]
    }
  },
  {
    id: 'vim-010',
    title: 'Delete a Line',
    description: 'Delete the entire current line.',
    task: 'Delete the entire line where the cursor is located.',
    hint: 'Double a key to apply to the whole line.',
    example: 'cc deletes line and enters insert mode',
    answer: ['dd'],
    tips: [
      'dd deletes and copies (yanks) the line',
      '5dd deletes 5 lines'
    ],
    difficulty: 'beginner',
    xpReward: 10,
    commandRef: {
      command: 'dd',
      description: 'Delete (cut) the entire current line.',
      syntax: '[count]dd',
      options: [
        { flag: 'dd', description: 'Delete current line' },
        { flag: '5dd', description: 'Delete 5 lines starting from current' },
        { flag: 'd$', description: 'Delete from cursor to end of line' },
        { flag: 'd0', description: 'Delete from cursor to beginning of line' },
        { flag: 'D', description: 'Delete to end of line (same as d$)' },
        { flag: 'dw', description: 'Delete from cursor to start of next word' }
      ],
      examples: [
        { code: 'dd', description: 'Delete the current line' },
        { code: '5dd', description: 'Delete 5 lines' },
        { code: 'd$', description: 'Delete from cursor to end of line' },
        { code: 'dG', description: 'Delete from current line to end of file' }
      ]
    }
  },

  // INTERMEDIATE LESSONS (10)
  {
    id: 'vim-011',
    title: 'Copy (Yank) a Line',
    description: 'Copy the current line to the register (clipboard).',
    task: 'Copy the current line.',
    hint: 'Vim calls copying "yanking".',
    example: 'yw yanks a word',
    answer: ['yy', 'Y'],
    tips: [
      'yy yanks the entire line',
      '5yy yanks 5 lines'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'yy',
      description: 'Yank (copy) text to a register for later pasting.',
      syntax: '[count]yy | y{motion}',
      options: [
        { flag: 'yy', description: 'Yank current line' },
        { flag: 'Y', description: 'Yank current line (same as yy)' },
        { flag: '5yy', description: 'Yank 5 lines' },
        { flag: 'yw', description: 'Yank from cursor to start of next word' },
        { flag: 'y$', description: 'Yank from cursor to end of line' },
        { flag: 'yiw', description: 'Yank inner word (word under cursor)' },
        { flag: '"ayy', description: 'Yank line into register a' }
      ],
      examples: [
        { code: 'yy', description: 'Copy the current line' },
        { code: '5yy', description: 'Copy 5 lines starting from current' },
        { code: 'yiw', description: 'Copy the word under the cursor' },
        { code: 'y$', description: 'Copy from cursor to end of line' }
      ]
    }
  },
  {
    id: 'vim-012',
    title: 'Paste',
    description: 'Paste the yanked or deleted text.',
    task: 'Paste the previously copied/deleted text after the cursor.',
    hint: 'A single key pastes text.',
    example: 'P pastes before cursor',
    answer: ['p'],
    tips: [
      'p = paste after cursor/line',
      'P = paste before cursor/line'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'p',
      description: 'Put (paste) text from a register after or before the cursor.',
      syntax: '[count]p | [count]P',
      options: [
        { flag: 'p', description: 'Paste after cursor (or below current line for lines)' },
        { flag: 'P', description: 'Paste before cursor (or above current line for lines)' },
        { flag: '3p', description: 'Paste 3 times' },
        { flag: '"ap', description: 'Paste from register a' },
        { flag: '"+p', description: 'Paste from system clipboard' },
        { flag: ':reg', description: 'Show contents of all registers' }
      ],
      examples: [
        { code: 'p', description: 'Paste after the cursor' },
        { code: 'P', description: 'Paste before the cursor' },
        { code: '5p', description: 'Paste the same text 5 times' },
        { code: '"+p', description: 'Paste from system clipboard' }
      ]
    }
  },
  {
    id: 'vim-013',
    title: 'Undo',
    description: 'Undo the last change.',
    task: 'Undo the most recent edit.',
    hint: 'A single key undoes changes.',
    example: 'U undoes all changes on current line',
    answer: ['u'],
    tips: [
      'u undoes one change at a time',
      'Vim has unlimited undo history'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'u',
      description: 'Undo changes in the current buffer.',
      syntax: '[count]u | U',
      options: [
        { flag: 'u', description: 'Undo last change' },
        { flag: '5u', description: 'Undo last 5 changes' },
        { flag: 'U', description: 'Undo all changes on current line' },
        { flag: ':earlier 5m', description: 'Undo to state 5 minutes ago' },
        { flag: ':earlier 10', description: 'Undo 10 changes' },
        { flag: ':undolist', description: 'Show undo history' }
      ],
      examples: [
        { code: 'u', description: 'Undo the last change' },
        { code: '5u', description: 'Undo the last 5 changes' },
        { code: 'U', description: 'Undo all changes made on this line' },
        { code: ':earlier 5m', description: 'Restore file to state 5 minutes ago' }
      ]
    }
  },
  {
    id: 'vim-014',
    title: 'Redo',
    description: 'Redo an undone change.',
    task: 'Redo (reverse an undo).',
    hint: 'Use Ctrl with a key.',
    example: 'u = undo, ? = redo',
    answer: ['Ctrl+r', 'Ctrl-r', '<C-r>'],
    tips: [
      'Ctrl+r redoes what u undid',
      'Think: r for redo'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'Ctrl+r',
      description: 'Redo a previously undone change.',
      syntax: 'Ctrl+r',
      options: [
        { flag: 'Ctrl+r', description: 'Redo one undone change' },
        { flag: '5 Ctrl+r', description: 'Redo 5 undone changes' },
        { flag: ':redo', description: 'Alternative redo command' },
        { flag: ':later 5m', description: 'Redo to state 5 minutes later' }
      ],
      examples: [
        { code: 'Ctrl+r', description: 'Redo the last undone change' },
        { code: '3Ctrl+r', description: 'Redo the last 3 undone changes' },
        { code: ':later 10', description: 'Redo 10 changes' }
      ]
    }
  },
  {
    id: 'vim-015',
    title: 'Search Forward',
    description: 'Search for text in the file, moving forward.',
    task: 'Search for the word "error" in the file.',
    hint: 'Start search with a forward slash.',
    example: '/pattern - searches forward',
    answer: ['/error'],
    tips: [
      '/ starts forward search',
      'Press n for next match, N for previous'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: '/',
      description: 'Search forward for a pattern in the file.',
      syntax: '/{pattern}[/[offset]]',
      options: [
        { flag: '/pattern', description: 'Search forward for pattern' },
        { flag: '?pattern', description: 'Search backward for pattern' },
        { flag: '/pattern/e', description: 'Search and place cursor at end of match' },
        { flag: '*', description: 'Search forward for word under cursor' },
        { flag: '#', description: 'Search backward for word under cursor' },
        { flag: ':set hlsearch', description: 'Highlight all search matches' },
        { flag: ':nohlsearch', description: 'Turn off search highlighting' }
      ],
      examples: [
        { code: '/error', description: 'Search forward for "error"' },
        { code: '?warning', description: 'Search backward for "warning"' },
        { code: '/\\<word\\>', description: 'Search for exact word match' },
        { code: '*', description: 'Search forward for word at cursor' }
      ]
    }
  },
  {
    id: 'vim-016',
    title: 'Next Search Match',
    description: 'Jump to the next search match.',
    task: 'Go to the next occurrence of the search pattern.',
    hint: 'A single key jumps to next match.',
    example: 'N goes to previous match',
    answer: ['n'],
    tips: [
      'n = next match in search direction',
      'N = previous match'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'n',
      description: 'Jump to the next or previous search match.',
      syntax: 'n | N',
      options: [
        { flag: 'n', description: 'Go to next match in search direction' },
        { flag: 'N', description: 'Go to previous match (opposite direction)' },
        { flag: 'gn', description: 'Go to next match and select it' },
        { flag: 'gN', description: 'Go to previous match and select it' }
      ],
      examples: [
        { code: 'n', description: 'Jump to the next search match' },
        { code: 'N', description: 'Jump to the previous search match' },
        { code: '3n', description: 'Jump forward 3 matches' },
        { code: 'cgn', description: 'Change the next match (powerful for repeating)' }
      ]
    }
  },
  {
    id: 'vim-017',
    title: 'Substitute in Line',
    description: 'Replace text within the current line.',
    task: 'Replace the first occurrence of "old" with "new" in the current line.',
    hint: 'Use :s command with pattern.',
    example: ':s/foo/bar/g - replaces all in line',
    answer: [':s/old/new', ':s/old/new/'],
    tips: [
      ':s/old/new replaces first occurrence',
      'Add /g to replace all in line'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: ':s',
      description: 'Substitute (find and replace) text in the current line.',
      syntax: ':s/{pattern}/{replacement}/[flags]',
      options: [
        { flag: ':s/old/new', description: 'Replace first occurrence in line' },
        { flag: ':s/old/new/g', description: 'Replace all occurrences in line' },
        { flag: ':s/old/new/c', description: 'Confirm each replacement' },
        { flag: ':s/old/new/i', description: 'Case insensitive replacement' },
        { flag: ':s/old/new/gc', description: 'Replace all with confirmation' }
      ],
      examples: [
        { code: ':s/old/new', description: 'Replace first "old" with "new" in current line' },
        { code: ':s/old/new/g', description: 'Replace all "old" with "new" in current line' },
        { code: ':s/cat/dog/gc', description: 'Replace all with confirmation prompts' },
        { code: ':s/\\t/  /g', description: 'Replace tabs with spaces in line' }
      ]
    }
  },
  {
    id: 'vim-018',
    title: 'Substitute in Entire File',
    description: 'Replace all occurrences of text throughout the entire file.',
    task: 'Replace all occurrences of "foo" with "bar" in the entire file.',
    hint: 'Use % to apply to all lines.',
    example: ':1,10s/old/new/g - lines 1-10 only',
    answer: [':%s/foo/bar/g'],
    tips: [
      '% means all lines in file',
      '/g means global (all occurrences per line)'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: ':%s',
      description: 'Substitute text across the entire file or a range of lines.',
      syntax: ':[range]s/{pattern}/{replacement}/[flags]',
      options: [
        { flag: ':%s/old/new/g', description: 'Replace all in entire file' },
        { flag: ':1,10s/old/new/g', description: 'Replace in lines 1-10' },
        { flag: ':.,$s/old/new/g', description: 'Replace from current line to end' },
        { flag: ':%s/old/new/gc', description: 'Replace all with confirmation' },
        { flag: ":'<,'>s/old/new/g", description: 'Replace in visual selection' }
      ],
      examples: [
        { code: ':%s/foo/bar/g', description: 'Replace all "foo" with "bar" in file' },
        { code: ':%s/foo/bar/gc', description: 'Replace all with confirmation prompts' },
        { code: ':10,20s/old/new/g', description: 'Replace only in lines 10-20' },
        { code: ':%s/\\s\\+$//g', description: 'Remove trailing whitespace from all lines' }
      ]
    }
  },
  {
    id: 'vim-019',
    title: 'Enter Visual Mode',
    description: 'Enter visual mode to select text character by character.',
    task: 'Enter VISUAL mode to start selecting text.',
    hint: 'A single key enters visual mode.',
    example: 'V enters visual line mode',
    answer: ['v'],
    tips: [
      'v = character-wise visual',
      'V = line-wise visual',
      'Ctrl+v = block visual'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'v',
      description: 'Enter VISUAL mode to select text character by character.',
      syntax: 'v',
      options: [
        { flag: 'v', description: 'Enter character-wise visual mode' },
        { flag: 'V', description: 'Enter line-wise visual mode' },
        { flag: 'Ctrl+v', description: 'Enter block visual mode' },
        { flag: 'gv', description: 'Reselect last visual selection' },
        { flag: 'o', description: 'Move to other end of selection' }
      ],
      examples: [
        { code: 'v', description: 'Start selecting text character by character' },
        { code: 'viw', description: 'Select inner word' },
        { code: 'vi"', description: 'Select text inside double quotes' },
        { code: 'vip', description: 'Select inner paragraph' }
      ]
    }
  },
  {
    id: 'vim-020',
    title: 'Visual Line Mode',
    description: 'Select entire lines in visual mode.',
    task: 'Enter VISUAL LINE mode to select whole lines.',
    hint: 'Capital letter for line mode.',
    example: 'v selects characters',
    answer: ['V'],
    tips: [
      'V selects entire lines',
      'Use j/k to extend selection up/down'
    ],
    difficulty: 'intermediate',
    xpReward: 25,
    commandRef: {
      command: 'V',
      description: 'Enter VISUAL LINE mode to select entire lines.',
      syntax: 'V',
      options: [
        { flag: 'V', description: 'Enter line-wise visual mode' },
        { flag: 'Vj', description: 'Select current and next line' },
        { flag: 'Vk', description: 'Select current and previous line' },
        { flag: 'V}', description: 'Select to end of paragraph' },
        { flag: 'VG', description: 'Select from current line to end of file' }
      ],
      examples: [
        { code: 'V', description: 'Select the entire current line' },
        { code: 'Vjjd', description: 'Select 3 lines and delete them' },
        { code: 'Vjjy', description: 'Select 3 lines and yank (copy) them' },
        { code: "Vj:s/old/new/g", description: 'Select lines then substitute text' }
      ]
    }
  },

  // ADVANCED LESSONS (5)
  {
    id: 'vim-021',
    title: 'Show Line Numbers',
    description: 'Display line numbers in the left margin.',
    task: 'Enable line numbers in Vim.',
    hint: 'Use :set command.',
    example: ':set relativenumber - relative line numbers',
    answer: [':set number', ':set nu'],
    tips: [
      ':set number shows absolute numbers',
      ':set relativenumber shows relative to cursor'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: ':set number',
      description: 'Display or hide line numbers in the editor.',
      syntax: ':set [no]number | :set [no]relativenumber',
      options: [
        { flag: ':set number', description: 'Show absolute line numbers' },
        { flag: ':set nonumber', description: 'Hide line numbers' },
        { flag: ':set nu', description: 'Short form of :set number' },
        { flag: ':set relativenumber', description: 'Show relative line numbers' },
        { flag: ':set number relativenumber', description: 'Show hybrid line numbers' },
        { flag: ':set nu!', description: 'Toggle line numbers on/off' }
      ],
      examples: [
        { code: ':set number', description: 'Display absolute line numbers' },
        { code: ':set relativenumber', description: 'Display relative line numbers' },
        { code: ':set number relativenumber', description: 'Show current line absolute, others relative' },
        { code: ':set nonumber norelativenumber', description: 'Hide all line numbers' }
      ]
    }
  },
  {
    id: 'vim-022',
    title: 'Jump to Start/End of File',
    description: 'Move cursor to the beginning or end of the file.',
    task: 'Jump to the very last line of the file.',
    hint: 'Capital G without a number.',
    example: 'gg goes to first line',
    answer: ['G'],
    tips: [
      'G = last line',
      'gg = first line'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'G',
      description: 'Jump to the end or beginning of the file.',
      syntax: 'G | gg | [count]G',
      options: [
        { flag: 'G', description: 'Jump to last line of file' },
        { flag: 'gg', description: 'Jump to first line of file' },
        { flag: '50G', description: 'Jump to line 50' },
        { flag: 'H', description: 'Jump to top of screen' },
        { flag: 'M', description: 'Jump to middle of screen' },
        { flag: 'L', description: 'Jump to bottom of screen' }
      ],
      examples: [
        { code: 'G', description: 'Go to the last line of the file' },
        { code: 'gg', description: 'Go to the first line of the file' },
        { code: '100G', description: 'Go to line 100' },
        { code: '50%', description: 'Go to 50% through the file' }
      ]
    }
  },
  {
    id: 'vim-023',
    title: 'Jump to Specific Line',
    description: 'Go directly to a specific line number.',
    task: 'Jump to line 42.',
    hint: 'Type the number before G.',
    example: '1G goes to line 1',
    answer: ['42G', ':42'],
    tips: [
      'numberG jumps to that line',
      ':number also works'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: '[count]G',
      description: 'Jump directly to a specific line number.',
      syntax: '[count]G | :[count]',
      options: [
        { flag: '42G', description: 'Jump to line 42' },
        { flag: ':42', description: 'Alternative: jump to line 42' },
        { flag: '1G', description: 'Jump to first line (same as gg)' },
        { flag: ':$', description: 'Jump to last line (same as G)' },
        { flag: ":'a", description: 'Jump to line with mark a' }
      ],
      examples: [
        { code: '42G', description: 'Jump to line 42' },
        { code: ':42', description: 'Alternative way to jump to line 42' },
        { code: ':+5', description: 'Jump 5 lines down from current' },
        { code: ':-3', description: 'Jump 3 lines up from current' }
      ]
    }
  },
  {
    id: 'vim-024',
    title: 'Change Inner Word',
    description: 'Delete a word and enter insert mode to replace it.',
    task: 'Delete the word under the cursor and start typing its replacement.',
    hint: 'Use c (change) with iw (inner word).',
    example: 'caw changes around word (includes space)',
    answer: ['ciw'],
    tips: [
      'ciw = change inner word',
      'Powerful with any text object: ci", ci(, etc.'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'ciw',
      description: 'Change inner word - delete word and enter insert mode.',
      syntax: 'c{motion} | ci{text-object} | ca{text-object}',
      options: [
        { flag: 'ciw', description: 'Change inner word (word only)' },
        { flag: 'caw', description: 'Change around word (includes surrounding space)' },
        { flag: 'ci"', description: 'Change inside double quotes' },
        { flag: "ci'", description: 'Change inside single quotes' },
        { flag: 'ci(', description: 'Change inside parentheses' },
        { flag: 'ci{', description: 'Change inside curly braces' },
        { flag: 'cit', description: 'Change inside HTML/XML tag' }
      ],
      examples: [
        { code: 'ciw', description: 'Delete word under cursor and enter insert mode' },
        { code: 'ci"', description: 'Change text inside double quotes' },
        { code: 'ci(', description: 'Change text inside parentheses' },
        { code: 'cit', description: 'Change content inside HTML tag' }
      ]
    }
  },
  {
    id: 'vim-025',
    title: 'Delete Around Word',
    description: 'Delete a word including surrounding whitespace.',
    task: 'Delete the word under cursor AND the space after it.',
    hint: 'Use d with aw (around word).',
    example: 'diw deletes just the word',
    answer: ['daw'],
    tips: [
      'daw = delete around word',
      'aw includes trailing whitespace'
    ],
    difficulty: 'advanced',
    xpReward: 50,
    commandRef: {
      command: 'daw',
      description: 'Delete around word - removes word and surrounding whitespace.',
      syntax: 'd{motion} | di{text-object} | da{text-object}',
      options: [
        { flag: 'diw', description: 'Delete inner word (word only)' },
        { flag: 'daw', description: 'Delete around word (includes surrounding space)' },
        { flag: 'di"', description: 'Delete inside double quotes' },
        { flag: 'da"', description: 'Delete around double quotes (includes quotes)' },
        { flag: 'di(', description: 'Delete inside parentheses' },
        { flag: 'da(', description: 'Delete around parentheses (includes parens)' },
        { flag: 'dip', description: 'Delete inner paragraph' }
      ],
      examples: [
        { code: 'daw', description: 'Delete word and surrounding whitespace' },
        { code: 'diw', description: 'Delete just the word under cursor' },
        { code: 'da"', description: 'Delete quoted string including quotes' },
        { code: 'dap', description: 'Delete paragraph and blank line' }
      ]
    }
  },

  // EXPERT LESSONS (5)
  {
    id: 'vim-026',
    title: 'Record a Macro',
    description: 'Record a sequence of commands to replay later.',
    task: 'Start recording a macro into register "a".',
    hint: 'Use q followed by the register letter.',
    example: 'qb starts recording to register b',
    answer: ['qa'],
    tips: [
      'qa starts recording to register a',
      'Press q again to stop recording'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'q{register}',
      description: 'Record a macro - a sequence of commands to replay later.',
      syntax: 'q{a-z} ... q',
      options: [
        { flag: 'qa', description: 'Start recording macro into register a' },
        { flag: 'q', description: 'Stop recording (when recording is active)' },
        { flag: 'qA', description: 'Append to existing macro in register a' },
        { flag: ':reg a', description: 'View contents of macro register a' },
        { flag: '"ap', description: 'Paste macro contents (for editing)' }
      ],
      examples: [
        { code: 'qa', description: 'Start recording a macro into register a' },
        { code: 'q', description: 'Stop recording the current macro' },
        { code: 'qajjddq', description: 'Record: move down 2 lines and delete line' },
        { code: '"ap', description: 'Paste the macro text to edit it' }
      ]
    }
  },
  {
    id: 'vim-027',
    title: 'Play a Macro',
    description: 'Execute a recorded macro.',
    task: 'Play the macro stored in register "a".',
    hint: 'Use @ followed by the register.',
    example: '5@a plays macro a five times',
    answer: ['@a'],
    tips: [
      '@a plays macro in register a',
      '@@ repeats the last played macro'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: '@{register}',
      description: 'Execute (play) a recorded macro from a register.',
      syntax: '[count]@{register} | @@',
      options: [
        { flag: '@a', description: 'Play macro stored in register a' },
        { flag: '@@', description: 'Repeat last played macro' },
        { flag: '5@a', description: 'Play macro a five times' },
        { flag: '100@a', description: 'Play macro a 100 times' },
        { flag: ':5,10normal @a', description: 'Run macro on lines 5-10' }
      ],
      examples: [
        { code: '@a', description: 'Play the macro stored in register a' },
        { code: '@@', description: 'Repeat the last executed macro' },
        { code: '10@a', description: 'Execute macro a ten times' },
        { code: ":'<,'>normal @a", description: 'Run macro on each line in selection' }
      ]
    }
  },
  {
    id: 'vim-028',
    title: 'Delete Matching Lines',
    description: 'Delete all lines that match a pattern.',
    task: 'Delete all lines containing the word "debug".',
    hint: 'Use :g (global) command with d.',
    example: ':g/pattern/d deletes matching lines',
    answer: [':g/debug/d'],
    tips: [
      ':g/pattern/d = global delete',
      ':v/pattern/d = delete lines NOT matching'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: ':g',
      description: 'Execute a command on all lines matching a pattern (global command).',
      syntax: ':g/{pattern}/{command}',
      options: [
        { flag: ':g/pattern/d', description: 'Delete all lines matching pattern' },
        { flag: ':v/pattern/d', description: 'Delete lines NOT matching pattern' },
        { flag: ':g/pattern/p', description: 'Print all matching lines' },
        { flag: ':g/pattern/normal @a', description: 'Run macro on matching lines' },
        { flag: ':g/^$/d', description: 'Delete all blank lines' },
        { flag: ':g/pattern/m$', description: 'Move matching lines to end of file' }
      ],
      examples: [
        { code: ':g/debug/d', description: 'Delete all lines containing "debug"' },
        { code: ':v/error/d', description: 'Keep only lines containing "error"' },
        { code: ':g/^$/d', description: 'Delete all empty lines' },
        { code: ':g/TODO/p', description: 'Print all lines with TODO comments' }
      ]
    }
  },
  {
    id: 'vim-029',
    title: 'Set a Mark',
    description: 'Mark a position to jump back to later.',
    task: 'Set a mark named "a" at the current cursor position.',
    hint: 'Use m followed by the mark name.',
    example: '`a jumps back to mark a',
    answer: ['ma'],
    tips: [
      'ma sets mark a at cursor',
      '`a jumps to exact position, \'a to line start'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'm{register}',
      description: 'Set a mark at the current cursor position to return to later.',
      syntax: 'm{a-zA-Z}',
      options: [
        { flag: 'ma', description: 'Set local mark a (file-specific)' },
        { flag: 'mA', description: 'Set global mark A (works across files)' },
        { flag: '`a', description: 'Jump to exact position of mark a' },
        { flag: "'a", description: 'Jump to beginning of line with mark a' },
        { flag: ':marks', description: 'List all marks' },
        { flag: ':delmarks a', description: 'Delete mark a' }
      ],
      examples: [
        { code: 'ma', description: 'Set mark "a" at current cursor position' },
        { code: '`a', description: 'Jump back to exact position of mark a' },
        { code: "'a", description: 'Jump to start of line containing mark a' },
        { code: "d'a", description: 'Delete from current line to line with mark a' }
      ]
    }
  },
  {
    id: 'vim-030',
    title: 'Complex Find and Replace',
    description: 'Use regex capture groups in substitution.',
    task: 'Swap first two words on each line (word1 word2 -> word2 word1) using capture groups.',
    hint: 'Use \\( \\) for capture groups and \\1 \\2 for backreferences.',
    example: ':%s/\\(foo\\)/[\\1]/g - surrounds foo with brackets',
    answer: [
      ':%s/\\(\\w\\+\\) \\(\\w\\+\\)/\\2 \\1/g',
      ':%s/\\(\\S\\+\\) \\(\\S\\+\\)/\\2 \\1/g'
    ],
    tips: [
      '\\( \\) captures groups',
      '\\1 \\2 references captured groups'
    ],
    difficulty: 'expert',
    xpReward: 100,
    commandRef: {
      command: 'Regex Substitution',
      description: 'Use regular expressions with capture groups for advanced find and replace.',
      syntax: ':%s/\\({pattern}\\)/\\1/g',
      options: [
        { flag: '\\( \\)', description: 'Capture group - saves matched text' },
        { flag: '\\1, \\2', description: 'Backreference - inserts captured group' },
        { flag: '\\w\\+', description: 'Match one or more word characters' },
        { flag: '\\s\\+', description: 'Match one or more whitespace characters' },
        { flag: '\\zs, \\ze', description: 'Set start/end of match' },
        { flag: '&', description: 'Entire matched text in replacement' }
      ],
      examples: [
        { code: ':%s/\\(\\w\\+\\) \\(\\w\\+\\)/\\2 \\1/g', description: 'Swap first two words on each line' },
        { code: ':%s/\\(foo\\)/[\\1]/g', description: 'Surround "foo" with brackets' },
        { code: ':%s/<\\(\\w\\+\\)>/<\\1><\\/\\1>/g', description: 'Convert <tag> to <tag></tag>' },
        { code: ':%s/\\v(\\w+) (\\w+)/\\2 \\1/g', description: 'Same swap using very magic mode' }
      ]
    }
  }
];

export default vimLessons;
